import { ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { RetainedEarningsService } from './retained-earnings.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-retained-earnings',
    templateUrl: './retained-earnings.component.html',
    styleUrls: ['./retained-earnings.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [DecimalPipe],
    standalone: false
})
export class RetainedEarningsComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly contextSubs = new Subscription();
  private isInitialized = false;
  /** Ignores stale HTTP callbacks after workspace/tab or global context refresh. */
  private contextEpoch = 0;

  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;
  modalTitle = 'Retained Earnings / Capital Account Reconciliation';

  entityList: any[] = [];
  rowData: any = {};
  filterParams: any = {};
  globalService: any;
  GENERAL_CONFIG: any;
  baseURLs: any = {};
  userSettings: any;

  cyopen = false;
  pyopen = false;
  cy_local_acct_dtls: any[] = [];
  py_local_acct_dtls: any[] = [];
  cy_local_acct_amt = 0;
  py_local_acct_amt = 0;

  oldExplanation = '';
  newExplanation = '';
  username = '';

  private dataLoadingStates = {
    entityData: false,
    formData: false
  };

  /** Guard to prevent multiple f8ovay (loadDetails) API calls until context is reset. */
  private detailsLoadStarted = false;

  constructor(
    private retainedEarningsService: RetainedEarningsService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private decimalPipe: DecimalPipe,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) { }

  ngOnInit(): void {
    this.resetAndTryInit();
    this.contextSubs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.resetAndTryInit();
      })
    );
    this.contextSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.resetAndTryInit();
      })
    );
  }

  ngOnDestroy(): void {
    this.contextEpoch++;
    this.contextSubs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) {
      fp = extractObject(hydrated);
    }
    this.filterParams = fp;
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    this.globalService = {
      ...shell,
      globalParams: mergedGp,
      filterParams: fp,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      getScenario: (ty: any) => this.gtwGlobal.getScenario(ty),
      getEIN: () => this.gtwGlobal.getEIN(),
      getTAXYEAR: () => this.gtwGlobal.getTAXYEAR(),
      getChartType: () => this.gtwGlobal.getChartType(),
      inputs: this.gtwGlobal.inputs,
      sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? ''
    };
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };
    this.baseURLs = gc;
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow)
      ? extractObject(rawRow[0]?.data ?? rawRow[0])
      : extractObject(rawRow);
    this.userSettings = this.gtwApp.USER_SETTINGS || {};
    if (this.userSettings?.user) {
      this.username = 'By ' + (this.userSettings.user.first_name || '') + ' ' +
        (this.userSettings.user.last_name || '') + ' On: ' + new Date() + '\n';
    }
  }

  private resetAndTryInit(): void {
    this.contextEpoch++;
    this.isInitialized = false;
    this.detailsLoadStarted = false;
    this.dataLoadingStates.entityData = false;
    this.dataLoadingStates.formData = false;
    this.applyWorkspaceContext();
    if (this.rowData && Object.keys(this.rowData).length > 0) {
      this.setupEntityData();
      this.showFormLoader = false;
    } else {
      this.showFormLoader = true;
    }
    this.tryInit();
    this.cdr.markForCheck();
  }

  private tryInit(): void {
    if (this.isInitialized) {
      return;
    }
    if (!this.GENERAL_CONFIG?.base_url || !this.rowData || Object.keys(this.rowData).length === 0) {
      return;
    }
    if (this.detailsLoadStarted) {
      return;
    }
    const gp = this.globalService?.globalParams || {};
    const fp = this.filterParams || {};
    const hasFilingKey = this.rowData.CONSOL_GROUP_KEY != null || this.rowData.FILING_KEY != null
      || fp.filing_key != null || gp.filing_key != null;
    const hasParentMeString = (fp.parent_me_string != null && fp.parent_me_string !== '')
      || (gp.parent_me_string != null && gp.parent_me_string !== '');
    if (!hasFilingKey || !hasParentMeString) {
      return;
    }
    this.isInitialized = true;
    this.detailsLoadStarted = true;
    this.loadDetails();
  }

  private setupEntityData(): void {
    if (!this.rowData || Object.keys(this.rowData).length === 0) {
      return;
    }
    this.oldExplanation = this.rowData.EXPLANATION != null ? this.rowData.EXPLANATION : '';
    this.entityList = [
      { key: 'Tax Year', value: this.rowData.TAX_YEAR },
      { key: 'LEID', value: this.rowData.LEID },
      { key: 'LE Name', value: this.rowData.LE_NAME }
    ];
    this.dataLoadingStates.entityData = true;
    this.checkAllDataLoaded();
  }

  private checkAllDataLoaded(): void {
    const allLoaded = this.dataLoadingStates.entityData && this.dataLoadingStates.formData;
    if (allLoaded) {
      this.showFormLoader = false;
      this.cdr.detectChanges();
    }
  }

  loadDetails(): void {
    const epoch = this.contextEpoch;
    this.showFormLoader = true;
    this.dataLoadingStates.formData = false;
    const gp = this.globalService?.globalParams || {};
    const fp = this.filterParams || {};
    const explicitParams = {
      filing_key: this.rowData?.CONSOL_GROUP_KEY ?? this.rowData?.FILING_KEY ?? fp.filing_key ?? gp.filing_key,
      parent_me_string: fp.parent_me_string ?? gp.parent_me_string
    };
    this.retainedEarningsService.loadDetails(
      this.GENERAL_CONFIG.base_url,
      this.globalService,
      this.filterParams,
      this.rowData,
      explicitParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (epoch !== this.contextEpoch) {
          return;
        }
        if (data.callSuccess === '1') {
          let cntcy = 0, cntpy = 0;
          this.cy_local_acct_dtls = [];
          this.py_local_acct_dtls = [];
          this.cy_local_acct_amt = 0;
          this.py_local_acct_amt = 0;
          const jsonObj = data.jsonObject || data;
          const arr = Array.isArray(jsonObj) ? jsonObj : (typeof jsonObj === 'object' ? Object.keys(jsonObj).map(k => jsonObj[k]) : []);
          for (const val of arr) {
            if (val.CY_PY_FLAG === 'CY') {
              this.cy_local_acct_dtls[cntcy] = val;
              this.cy_local_acct_amt += Number(val.AMT) || 0;
              cntcy++;
            } else {
              this.py_local_acct_dtls[cntpy] = val;
              this.py_local_acct_amt += Number(val.AMT) || 0;
              cntpy++;
            }
          }
          this.rowData.PY_RE_AMT = this.py_local_acct_amt;
          this.rowData.CY_RE_AMT = this.cy_local_acct_amt;
          this.oldExplanation = this.rowData.EXPLANATION != null ? this.rowData.EXPLANATION : '';
          this.dataLoadingStates.formData = true;
        } else {
          if (data.errorMessage === 'no access') {
            this._snackBar.open('Sorry you do not have access to do the requested action.', 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          } else {
            this._snackBar.open('An error has occurred. If you continue to experience this issue please visit the help page to contact support.', 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          }
        }
        this.showFormLoader = false;
        this.checkAllDataLoaded();
        this.cdr.detectChanges();
      },
      (error: any) => {
        if (epoch !== this.contextEpoch) {
          return;
        }
        console.error('Error loading retained earnings details:', error);
        this.showFormLoader = false;
        this._snackBar.open('Error loading data. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  showDetailsCY(_index: number, value: boolean): void {
    this.cyopen = value;
    this.cdr.detectChanges();
  }

  showDetailsPY(_index: number, value: boolean): void {
    this.pyopen = value;
    this.cdr.detectChanges();
  }

  formatNumber(value: any): string {
    const n = value != null ? Number(value) : 0;
    return this.decimalPipe.transform(n, '1.0-3') || '0';
  }

  save(): void {
    const epoch = this.contextEpoch;
    this.isSaveClicked = true;
    this.crudLoading = true;
    this.retainedEarningsService.saveRetainedEarningsForm(
      this.GENERAL_CONFIG.base_url,
      this.rowData,
      this.newExplanation,
      this.oldExplanation,
      this.username,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        if (epoch !== this.contextEpoch) {
          this.isSaveClicked = false;
          this.crudLoading = false;
          return;
        }
        this.isSaveClicked = false;
        this.crudLoading = false;
        if (result.callSuccess === '1') {
          this._snackBar.open('Data saved Successfully.', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          if (this.newExplanation !== undefined && this.newExplanation !== '') {
            this.rowData.EXPLANATION = this.oldExplanation ? (this.username + ' \n' + this.newExplanation + ' \n' + this.oldExplanation) : (this.username + ' \n' + this.newExplanation);
          } else {
            this.rowData.EXPLANATION = this.oldExplanation;
          }
          this.oldExplanation = this.rowData.EXPLANATION;
          this.newExplanation = '';
          this.requestGridUpdate.emit('refresh');
        } else {
          this._snackBar.open(result.errorMessage || 'An error has occurred. If this error continues please go to the help page and contact support.', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        if (epoch !== this.contextEpoch) {
          this.isSaveClicked = false;
          this.crudLoading = false;
          return;
        }
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  onNewExplanationChange(value: string): void {
    this.newExplanation = value != null ? value : '';
  }
}
